import { MouseEventHandler, useCallback, useEffect, useState } from "react";
import { Pagination } from "../pagination/Pagination";

import { ArrowLongDownIcon, ArrowLongUpIcon } from "@heroicons/react/24/solid";

export type Action = {
  button: any;
  onClick: () => void;
};

export type RowData = {
  [key: string]: string | number | JSX.Element;
};

export type SortableRowData = {
  [key: string]: string | number | boolean;
};

export type GridCol = {
  field: string;
  headerName: string | JSX.Element;
  type?: "number" | "string";
  style: string;
  sort?: boolean;
};

export type GridRow = {
  rowClickHandler?: () => void | Promise<void>;
  rowData: RowData;
  sortableRowData?: SortableRowData;
  style?: string;
};

export type PageItem = {
  perPage: number;
  range: number;
  margin: number;
};

export type TableProps = {
  gridCol: GridCol[];
  gridRow: GridRow[];
  defaultSortKey?: GridCol["field"] | undefined;
  defaultSortOrder?: SortOrder | undefined;
  sort?: boolean;
  header?: boolean;
  footer?: boolean;
  height?: string;
  limit?: number;
  gridRowLength?: number;
  divider?: boolean;
  externalCurrentPage?: number;
  setExternalCurrentPage?: React.Dispatch<React.SetStateAction<number>>;
  pagingFunc?: (ver: number) => void;
  parentSetSortKey?: React.Dispatch<React.SetStateAction<string>>;
  parentSetSortOrder?: React.Dispatch<React.SetStateAction<string>>;
};

type SortOrder = "asc" | "desc";

const TableList = ({
  gridCol,
  gridRow,
  defaultSortKey,
  defaultSortOrder,
  sort = true,
  header = false,
  footer = true,
  divider = false,
  height= "",
  limit = 10,
  gridRowLength = 0,
  externalCurrentPage,
  setExternalCurrentPage,
  pagingFunc,
  parentSetSortKey,
  parentSetSortOrder
}: TableProps) => {

  const [sortKey, setSortKey] = useState<string>(defaultSortKey ?? '');
  const [sortOrder, setSortOrder] = useState<SortOrder>(defaultSortOrder ?? 'asc');

  const sortedData = useCallback(
    () => {
      if (!sortKey){
        return gridRow;
      }
      return gridRow.sort((a, b) => {
        const currentRowData = a.sortableRowData !== undefined && a.sortableRowData[sortKey] !== undefined
          ? a.sortableRowData[sortKey]
          : a.rowData[sortKey];
        const previewRowData = b.sortableRowData !== undefined && b.sortableRowData[sortKey] !== undefined
          ? b.sortableRowData[sortKey]
          : b.rowData[sortKey];
        if(currentRowData === previewRowData) {
          return 0;
        }
        if (sortOrder === "desc") {
          return currentRowData < previewRowData  ? 1 : -1;
        }
        return currentRowData > previewRowData  ? 1 : -1;
      });
    },
    [gridRow, sortKey, sortOrder]
  );

  const changeSort = (key: string) => {
    if (key === sortKey) {
      const order = sortOrder === "asc" ? "desc" : "asc";
      setSortOrder(order);
      if (parentSetSortOrder !== undefined) parentSetSortOrder(order.toUpperCase());
    }
    setSortKey(key);
    if (parentSetSortKey !== undefined) parentSetSortKey(key);
  }

  const [offset, setOffset] = useState(0);
  const [internalPage, setInternalPage] = useState(1);
  const currentPage = externalCurrentPage ?? internalPage;
  const setCurrentPage = setExternalCurrentPage ?? setInternalPage;

  const pages = {
    perPage: (!header && !footer ) ? 0 : limit,
    range: 5,
    margin: 2,
  };

  // 最初に全データ取得してからページ分割する画面の場合、データの長さを変数に格納
  if (gridRowLength === 0) {
    gridRowLength = gridRow.length;
  }

  const perPage = pages.perPage > 0 ? pages.perPage : gridRowLength;
  const [totalPage, setTotalPage] = useState(Math.ceil(gridRowLength / perPage))

  useEffect(() => {
    setTotalPage(Math.ceil(gridRowLength / perPage))
  }, [gridRow, gridRowLength])

  const handlePageChange = (page: number) => {
    setOffset((page - 1) * perPage);
    // ページ件数を親コンポーネントへ渡す
    if (pagingFunc !== undefined) {
      pagingFunc(page);
    }
  };

  // 表示ページ分のデータのみ取得した場合は分割する必要がないため、関数が存在しているかどうかで分岐させる
  const sortedData_ = (pagingFunc !== undefined) ? sortedData() : sortedData().slice(offset, offset + perPage);

  return (
    <div className="flex flex-col">
      {/* <Filter></Filter> */}
      <div className="overflow-x-auto">
        <div className="inline-block min-w-full p-1 align-middle">
          <div className="relative overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
            <table className={`min-w-full table-fixed divide-y divide-gray-${divider? "400": "300"}`}>
              <thead className="bg-gray-50 ">
                {header && (
                  <tr className={`${divider ? "divide-x divide-gray-400" : ''}`}>
                    <th
                      colSpan={gridCol.length}
                    >
                      <Pagination
                        className={'px-1 py-1 justify-end'}
                        pageCount={totalPage}
                        onChange={handlePageChange}
                        currentPage={currentPage}
                        setCurrentPage={setCurrentPage}
                      ></Pagination>
                    </th>
                  </tr>
                )}
                <tr className={`${divider ? "divide-x divide-gray-400" : ''}`}>
                  {gridCol.map((item, index) => {
                    const colSort = item.sort ?? true;
                    return (
                      <th
                        scope="col"
                        className={`whitespace-nowrap px-3 relative py-3.5 text-left text-sm font-semibold ${item.style != "text-right" && item.style != ""?item.style : ""}`}
                        key={`th-${index}-${item.field}`}
                      >
                        {sort && colSort ? (
                          <SortButton
                            label={item.headerName}
                            columnKey={item.field}
                            onClick={() => changeSort(item.field)}
                            {...{
                              sortOrder,
                              sortKey,
                            }}
                          />
                        ):item.headerName}
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody className={`divide-y divide-gray-${divider? "400": "300"} bg-white`}>
                {sortedData_
                  .map((item, index) => {
                    return (
                      <tr
                        key={`tr-${index}`}
                        className={`${divider ? "divide-x divide-gray-400" : ''} ${index % 2 === 0 ? '' : "bg-gray-50"}`}
                      >
                        {gridCol.map((col) => {
                          return (
                            <>
                            {col.style == "text-right" ? (
                              <>
                              <td
                                className={`whitespace-nowrap text-right px-3 py-4 text-sm text-gray-500 ${item.style}`}
                                key={`td-${index}-${col.field}`}
                                style={{paddingRight: '60px'}}
                              >
                                {item.rowData[col.field]}
                              </td>
                              </>
                            ):(
                              <>
                              <td
                                className={`whitespace-nowrap text-left px-3 py-4 text-sm text-gray-500 ${item.style}`}
                                key={`td-${index}-${col.field}`}
                              >
                                {item.rowData[col.field]}
                              </td>
                              </>
                            )}
                            </>
                          );
                        })}
                      </tr>
                    );
                  })}
              </tbody>
              {footer && (
                <tfoot>
                  <tr>
                    <td colSpan={gridCol.length} className="text-right">
                      <Pagination
                        className={'px-3 py-3 bg-white border-t justify-end'}
                        pageCount={totalPage}
                        onChange={handlePageChange}
                        currentPage={currentPage}
                        setCurrentPage={setCurrentPage}
                      ></Pagination>
                    </td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

const SortButton = ({
  label,
  sortOrder,
  columnKey,
  sortKey,
  onClick,
}: {
  label: string | JSX.Element;
  sortOrder: SortOrder;
  columnKey: string;
  sortKey: string;
  onClick: MouseEventHandler<HTMLButtonElement>;
}) => {
  return (
    <button
      onClick={onClick}
      className={`${
        sortKey === columnKey && sortOrder === "desc"
          ? "sort-button sort-reverse "
          : "sort-button"
      }`}
    >
      <div className="group inline-flex ">
        {label}
        <span
          className={`ml-2 flex-none rounded  group-hover:text-gray-400 ${
            sortKey === columnKey ? "text-gray-70" : "text-gray-50 "
          }`}
        >
          {sortOrder === "asc" ? (
            <ArrowLongUpIcon className="h-5 w-5" aria-hidden="true" />
          ) : (
            <ArrowLongDownIcon className="h-5 w-5" aria-hidden="true" />
          )}
        </span>
      </div>
    </button>
  );
}

export default TableList;
